import { useEffect, useRef } from 'react';
import { Dispatch } from 'react';
import { AppRouteAction, SearchItem } from '../types';

interface UseDataReadyStateProps {
  searchItems: SearchItem[];
  mappingContent: any;
  isLoading: boolean;
  view: string;
  source: string;
  tab: string;
  dispatch: Dispatch<AppRouteAction>;
  pathname: string;
  search: string;
}

export const useDataReadyState = ({
  searchItems,
  mappingContent,
  isLoading,
  view,
  source,
  tab,
  dispatch,
  pathname,
  search,
}: UseDataReadyStateProps) => {
  const prevDataRef = useRef<{
    searchItems: SearchItem[];
    mappingContent: any;
    view: string;
    source: string;
    tab: string;
  } | null>(null);

  const dataReadyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (dataReadyTimeoutRef.current) {
      clearTimeout(dataReadyTimeoutRef.current);
    }

    if (isLoading) {
      dispatch({ type: 'SET_IS_DATA_READY', payload: false });
      return;
    }

    const isSearchRoute = pathname === '/search' || pathname === '/examples';
    const params = new URLSearchParams(search);

    let hasRequiredData: boolean;

    if (isSearchRoute && params.toString() !== '') {
      const urlView = params.get('view') || view;
      const urlIdents = params.get('idents');
      const effectiveView = urlView || view;

      const isAllBrowseFromUrl = urlView === 'ALL' && (!urlIdents || urlIdents === 'ALL');

      const hasEntityFilterInUrl =
        !!effectiveView && effectiveView !== 'ALL' && !!urlIdents && urlIdents !== 'ALL';

      hasRequiredData = Boolean(
        mappingContent &&
        effectiveView &&
        source &&
        tab &&
        (isAllBrowseFromUrl || searchItems.length > 0 || hasEntityFilterInUrl),
      );
    } else {
      hasRequiredData = Boolean(
        mappingContent && view && source && tab && (view === 'ALL' || searchItems.length > 0),
      );
    }

    if (!hasRequiredData) {
      dispatch({ type: 'SET_IS_DATA_READY', payload: false });
      return;
    }

    const currentData = {
      searchItems,
      mappingContent,
      view,
      source,
      tab,
    };

    const dataChanged =
      !prevDataRef.current || JSON.stringify(prevDataRef.current) !== JSON.stringify(currentData);

    if (dataChanged) {
      dispatch({ type: 'SET_IS_DATA_READY', payload: false });

      dataReadyTimeoutRef.current = setTimeout(() => {
        dispatch({ type: 'SET_IS_DATA_READY', payload: true });
        // Ensure loading is turned off when data is ready
        dispatch({ type: 'SET_LOADING', payload: false });
        prevDataRef.current = currentData;
      }, 100);
    } else if (hasRequiredData) {
      dispatch({ type: 'SET_IS_DATA_READY', payload: true });
      // Ensure loading is turned off when data is ready
      dispatch({ type: 'SET_LOADING', payload: false });
    }

    return () => {
      if (dataReadyTimeoutRef.current) {
        clearTimeout(dataReadyTimeoutRef.current);
      }
    };
  }, [searchItems, mappingContent, isLoading, view, source, tab, dispatch, pathname, search]);

  useEffect(() => {
    return () => {
      if (dataReadyTimeoutRef.current) {
        clearTimeout(dataReadyTimeoutRef.current);
      }
    };
  }, []);
};
